/**
 * Document Restore (issue #66 follow-up)
 *
 * When the proxy request handler redirects the user to login because the
 * TYPO3 session expired, the in-memory document is lost. We snapshot
 * document metadata to sessionStorage just before redirecting and use it
 * on the next app init to inform the user that they need to reload.
 *
 * sessionStorage is per-tab, so this never leaks state across tabs.
 */

import { logger } from '../logger';
import { STORAGE_KEYS } from '../app-constants';

const STORAGE_KEY = STORAGE_KEYS.RESTORE_DOCUMENT;

export interface DocumentRestoreMetadata {
  name: string;
  size?: number;
  type?: string;
  timestamp: number;
}

/**
 * Snapshot document metadata before a forced auth redirect so the user
 * can be informed to reload after landing back on the SPA.
 */
export function saveDocumentForRestore(
  doc: { name: string; size?: number; type?: string } | null | undefined
): void {
  if (typeof sessionStorage === 'undefined' || !doc) return;
  try {
    const metadata: DocumentRestoreMetadata = {
      name: doc.name,
      size: doc.size,
      type: doc.type,
      timestamp: Date.now(),
    };
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(metadata));
    logger.debug('Saved document metadata for restore:', doc.name);
  } catch (err) {
    logger.warn('Could not snapshot document before redirect:', err);
  }
}

/**
 * Read and clear previously saved document metadata. Returns null if
 * there is nothing to restore or the payload is unparseable.
 */
export function loadSavedDocument(): DocumentRestoreMetadata | null {
  if (typeof sessionStorage === 'undefined') return null;
  const raw = sessionStorage.getItem(STORAGE_KEY);
  if (!raw) return null;
  sessionStorage.removeItem(STORAGE_KEY);
  try {
    return JSON.parse(raw) as DocumentRestoreMetadata;
  } catch (err) {
    logger.warn('Could not restore saved document:', err);
    return null;
  }
}
